import { stemmer } from "stemmer";

/**
 * Tokenize a string into lowercase words
 * Keeps apostrophes (for possessives/contractions), removes other punctuation
 * 
 * @param {string} str - Input string
 * @param {number} minLength - Minimum word length to keep (default 2)
 */
export function tokenize(str, minLength = 2) {
  if (!str) return [];
  return str
    .toLowerCase()
    .replace(/[^a-z0-9'\s]/g, " ") // Keep alphanumeric, apostrophes, and spaces
    .split(/\s+/)
    .filter((word) => word.length >= minLength);
}

/**
 * Normalize a string for exact comparison
 * Tokenizes and joins with single space for consistent comparison
 */
function normalizeForComparison(str) {
  return tokenize(str).join(' ');
}

/**
 * Build a precomputed index for the catalog
 * This should be called once when the catalog loads, not on every search
 * 
 * @param {Array} catalog - Array of catalog items with { id, name, ... }
 * @returns {Array} Indexed catalog with precomputed tokens and stems
 */
export function buildCatalogIndex(catalog) {
  if (!catalog || catalog.length === 0) return [];
  
  return catalog.map(item => {
    const tokens = tokenize(item.name);
    const stemmed = tokens.map(w => stemmer(w));
    return {
      item,
      tokens,
      stemmed,
    };
  });
}

/**
 * Score a catalog item against a search query
 * 
 * Returns a score > 0 only if there's meaningful overlap between query and item.
 * 
 * Scoring weights:
 * - Stemmed word match: +10 per matching word
 * - Last word match (stemmed): +25 bonus
 * - Last word match (exact): +15 additional bonus
 * - Same word count: +5 bonus
 * - Prefix match (for partial typing): +3 per match
 */
function scoreItem(query, itemName) {
  const queryWords = tokenize(query);
  const itemWords = tokenize(itemName);
  
  if (queryWords.length === 0 || itemWords.length === 0) return 0;
  
  // Stem all words for comparison
  const queryStemmed = queryWords.map((w) => stemmer(w));
  const itemStemmed = itemWords.map((w) => stemmer(w));
  
  return scoreWithPrecomputed(queryWords, queryStemmed, itemWords, itemStemmed);
}

/**
 * Score using precomputed tokens and stems
 * This is the core scoring logic, separated so it can be used with precomputed data
 */
function scoreWithPrecomputed(queryWords, queryStemmed, itemWords, itemStemmed) {
  if (queryWords.length === 0 || itemWords.length === 0) return 0;
  
  // Exact normalized match gets highest priority
  const queryNormalized = queryWords.join(' ');
  const itemNormalized = itemWords.join(' ');
  if (queryNormalized === itemNormalized) {
    return 1000;  // Skip all other scoring
  }
  
  let score = 0;
  // Check if item starts with query (prefix match)
  // Start with full query, then keep dropping last word until only one word remains
  let wordsDropped = 0;
  let currentQueryWords = queryWords;

/**
 * General overview of the scoring logic for catalog search:
 * High score = high relevance, will show up higher in the search results
 * 
 * Conditions are evaluated in order. When a condition is met, its logic runs and no other 
 * conditions are checked for that catalog item (a break statement is executed within the condition).
 * 
 * First condition: Catalog items that start with the user query search are scored higher, 
 * then receive bonus points if:
 * - The number of words in the query and the catalog item match: +4
 * - The original user search query has no words dropped from it (wordsDropped === 0): +3
 * 
 * Second condition: If there is no startsWith match, we take the stemmed string of the catalog 
 * item and check if it starts with the stemmed user search query string.
 * 
 * Third condition: If the stemmed string comparison fails, we check if the last word of the 
 * user query (stemmed) is in the catalog item stemmed array: +2
 * 
 * Fourth condition: If the previous conditions fail, and we are on the last word of the user 
 * query after dropping words (check the word dropping logic below), we check if each stemmed 
 * word in the user query stemmed array is in the catalog item stemmed array: +1
 * 
 * Dropping logic: If none of the conditions that are above are met the last word that is in the
 * currentQueryWords array is dropped and then we run through all the conditions that are above again.
 */  
  while (currentQueryWords.length >= 1) {
    const currentQueryNormalized = currentQueryWords.join(' ');
    // Frist condition
    if (itemNormalized.startsWith(currentQueryNormalized)) {
      // currentQueryWords.length is the number of words still in the query after dropping
      score += currentQueryWords.length + 2;
      
      // Check if token lengths match (exact word count match) - only for full query
      if (wordsDropped === 0 && (queryWords.length === itemWords.length)) {
        // Bonus points (points that are added to the score)
        score += 4;
      } else if (wordsDropped === 0) {
        // Bonus points (points that are added to the score)
        score += 3;
      }
      break;
    // Second condition
    } else if (itemStemmed.join(' ').startsWith(queryStemmed.slice(0, currentQueryWords.length).join(' '))) {
      score += currentQueryWords.length + 2;
      break;
    // Third condition
    } else if (itemStemmed.includes(queryStemmed[queryStemmed.length - 1])) {
      score += 2;
      break;
    // Fourth condition
    } else if (currentQueryWords.length === 1) {
      for (const qWord of queryStemmed) {
        if (itemStemmed.includes(qWord)) {
          score += 1;
          break;
        }
      }
    }

    // Drop the last word and continue
    currentQueryWords = currentQueryWords.slice(0, -1);
    wordsDropped++;
  }

  return score;
}

/**
 * Search the catalog for items matching the query
 * 
 * @param {string} query - The search query
 * @param {Array} catalog - Array of catalog items with { id, name, ... }
 * @param {number} limit - Maximum results to return (default 20)
 * @returns {Array} Ranked catalog items sorted by relevance
 */
export function searchCatalog(query, catalog, limit = 20) {
  if (!query || !query.trim() || !catalog || catalog.length === 0) {
    return [];
  }
  
  const trimmedQuery = query.trim();
  
  // Score and filter items
  const results = [];
  for (const item of catalog) {
    const score = scoreItem(trimmedQuery, item.name);
    if (score > 0) {
      results.push({ item, score });
    }
  }
  
  // Sort by score descending, take top N
  results.sort((a, b) => b.score - a.score);
  
  return results.slice(0, limit).map((r) => r.item);
}

/**
 * Search using a precomputed catalog index (optimized for repeated searches)
 * Only tokenizes/stems the query - catalog items are already processed
 * 
 * @param {string} query - The search query
 * @param {Array} catalogIndex - Precomputed index from buildCatalogIndex()
 * @param {number} limit - Maximum results to return (default 20)
 * @returns {Array} Ranked catalog items sorted by relevance
 */
export function searchCatalogIndexed(query, catalogIndex, limit = 20) {
  if (!query || !query.trim() || !catalogIndex || catalogIndex.length === 0) {
    return [];
  }
  
  const trimmedQuery = query.trim();
  
  // Tokenize and stem the query ONCE
  const queryWords = tokenize(trimmedQuery);
  const queryStemmed = queryWords.map((w) => stemmer(w));
  
  if (queryWords.length === 0) return [];
  
  // Score against precomputed catalog data
  const results = [];
  for (const indexed of catalogIndex) {
    const score = scoreWithPrecomputed(
      queryWords,
      queryStemmed,
      indexed.tokens,
      indexed.stemmed
    );
    if (score > 0) {
      results.push({ item: indexed.item, score });
    }
  }
  
  // Sort by score descending, take top N
  results.sort((a, b) => b.score - a.score);
  
  return results.slice(0, limit).map((r) => r.item);
}

/**
 * Quick check if any catalog items match the query
 * 
 * @param {string} query - The search query  
 * @param {Array} catalog - Array of catalog items
 * @returns {boolean} True if at least one match exists
 */
export function hasAnyMatch(query, catalog) {
  if (!query || !query.trim() || !catalog || catalog.length === 0) {
    return false;
  }
  
  const trimmedQuery = query.trim();
  
  for (const item of catalog) {
    if (scoreItem(trimmedQuery, item.name) > 0) {
      return true;
    }
  }
  
  return false;
}
